import { useEffect, useId, useRef, type FormEvent, type ReactNode } from "react";
import { X } from "lucide-react";

export function Dialog({ open, title, description, children, onClose, footer, wide = false, preventClose = false }: {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
  preventClose?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const close = () => onClose();
    dialog.addEventListener("close", close);
    return () => dialog.removeEventListener("close", close);
  }, [onClose]);
  const cancel = (event: React.SyntheticEvent) => { if (preventClose) event.preventDefault(); };
  return (
    <dialog ref={ref} onCancel={cancel} className={`m-auto max-h-[min(90vh,56rem)] w-[calc(100%-2rem)] overflow-hidden rounded-[0.875rem] border bg-[var(--color-surface-raised)] p-0 text-ink shadow-[var(--shadow-dialog)] backdrop:bg-[#0f1420]/60 ${wide ? "max-w-4xl" : "max-w-xl"}`} aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}>
      <div className="flex max-h-[min(90vh,56rem)] flex-col">
        <header className="flex items-start gap-4 border-b p-5">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-xl font-bold">{title}</h2>
            {description ? <p id={descriptionId} className="mt-1 text-sm text-muted">{description}</p> : null}
          </div>
          {!preventClose ? <button type="button" className="icon-btn -m-2" onClick={onClose} aria-label="关闭对话框"><X size={20} /></button> : null}
        </header>
        <div className="min-h-0 overflow-y-auto p-5">{children}</div>
        {footer ? <footer className="flex flex-wrap justify-end gap-3 border-t p-4">{footer}</footer> : null}
      </div>
    </dialog>
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel = "移到回收站", busy = false, onConfirm, onClose }: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const submit = (event: FormEvent) => { event.preventDefault(); void onConfirm(); };
  return (
    <Dialog open={open} title={title} description={description} onClose={onClose} preventClose={busy} footer={null}>
      <form onSubmit={submit} className="flex justify-end gap-3">
        <button type="button" className="btn-secondary" autoFocus onClick={onClose} disabled={busy}>取消</button>
        <button type="submit" className="btn-danger" disabled={busy}>{busy ? "正在处理…" : confirmLabel}</button>
      </form>
    </Dialog>
  );
}
